// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import Carousel from 'react-multi-carousel';
import { array, bool } from 'prop-types';
import { Loading, SkeletonPlaceholder } from '@carbon/react';

import Img from 'Containers/Image/Image';
import useScreen from 'Hooks/useScreen';
import ButtonGroup from 'Components/Dashboard/CarouselButtonGroup';
import { AnalyticsLink } from 'Components/shared';

import WindowsLaptop from 'Assets/images/Banner3.jpg';
import CopilotCampaign from 'Assets/images/Banner4.jpg';
import NIQBanner from 'Assets/images/banner-niq.jpg';

const StyledBrandsSlider = styled.div`
  position: relative;
  margin-top: 75px;

  .brandsSlider {
    margin: 0 -16px;

    .brand {
      display: flex;
      align-items: center;
      justify-content: center;
      height: auto;
      width: 100%;
      background-color: ${ props => props.theme.colors.white };
      padding: 0 16px;

      img {
        max-width: 100%;
        object-fit: contain;
      }
    }
  }
`;

const StyledBannerPlaceholder = styled(SkeletonPlaceholder)`
  width: calc(100% - 20px);
  aspect-ratio: 2;
  height: auto;
`;

const renderBackupBanners = () => (
  [
    <div className="brand" key="niq">
      <AnalyticsLink
        to="https://nielseniq.com/global/en/solutions/partner-network-become-a-partner/"
        target="_blank"
        event={ { action: 'niq_banner_clicked' } }
      >
        <img src={ NIQBanner } alt="NIQ" />
      </AnalyticsLink>
    </div>,
    <div className="brand" key="copilot">
      <AnalyticsLink
        to="https://startwithcopilotpc.campaignhubs.com/p/2"
        target="_blank"
        event={ { action: 'copilot_pc_banner_clicked' } }
      >
        <img src={ CopilotCampaign } alt="Copilot campaign" />
      </AnalyticsLink>
    </div>,
    <div className="brand" key="windows">
      <AnalyticsLink
        to="/products/laptopuri-c1722/attributes/sistem-de-operare-n6007,windows-11-pro-v33965/e/c"
        event={ { action: 'windows_laptops_banner_clicked' } }
      >
        <img src={ WindowsLaptop } alt="Windows 11" />
      </AnalyticsLink>
    </div>,
  ]
);

const BrandsSlider = ({ loading, promotions }) => {
  const { width } = useScreen();

  const getItems = () => {
    if (loading) {
      return [...Array(3).keys()].map(a => <StyledBannerPlaceholder key={ a } />);
    }

    if (promotions?.length > 0) {
      return promotions.map(banner => (
        <div className="brand" key={ banner.id }>
          <AnalyticsLink
            to={
              banner?.promotionId
                ? `/products?promotion=${ banner.promotionId }`
                : banner?.url
            }
            event={ { action: `${ banner?.title || 'unknown' }_banner_clicked` } }
          >
            <Img
              src={ banner.imageUrl }
              loader={ <Loading withOverlay={ false } active small /> }
              alt={ banner?.title || 'Banner' }
            />
          </AnalyticsLink>
        </div>
      ));
    }

    return renderBackupBanners();
  };

  return (
    <StyledBrandsSlider>
      <Carousel
        className="brandsSlider"
        ssr
        customButtonGroup={ <ButtonGroup /> }
        autoPlaySpeed={ 3000 }
        deviceType="desktop"
        showDots={ false }
        autoPlay={ false }
        renderButtonGroupOutside
        arrows={ false }
        slidesToSlide={ 1 }
        removeArrowOnDeviceType={ ['tablet', 'mobile'] }
        shouldResetAutoplay={ width > 767 }
        responsive={ {
          desktop: {
            breakpoint: { max: 3000, min: 1368 },
            items: 3
          },
          tablet: {
            breakpoint: { max: 1368, min: 767 },
            items: 2
          },
          mobile: {
            breakpoint: { max: 767, min: 0 },
            items: 1,
          }
        } }
      >
        { getItems() }
      </Carousel>
    </StyledBrandsSlider>
  );
};

BrandsSlider.propTypes = {
  loading: bool,
  promotions: array,
};

BrandsSlider.displayName = 'BrandsSlider';

export default BrandsSlider;
